<script>
  import { token } from "./store/token.js";
  import { fetch_user_list, fetch_device_types } from "./api/request";
  import { fly } from "svelte/transition";
  import UserDetail from "./UserDetail.svelte";
  import WorkspaceDetail from "./WorkspaceDetail.svelte";
  import { onMount } from "svelte";
  import { route, navigate } from "./store/router.js";

  const trialStatusOptions = [
    { value: '', label: 'All Trial Status' },
    { value: 'trial_active', label: 'Trial Active' },
    { value: 'trial_expired', label: 'Trial Expired' },
    { value: 'trial_abandoned', label: 'Trial Abandoned' },
  ];

  const DEVICE_CATEGORIES = [
    { value: '', label: 'All' },
    { value: 'mobile', label: 'Mobile' },
    { value: 'desktop', label: 'Desktop' },
    { value: 'web', label: 'Web' },
  ];

  // Maps device type string to category (mobile/desktop/web)
  const getDeviceCategory = (deviceType) => {
    const t = (deviceType || '').toLowerCase();
    if (/iphone|ipad|android|tablet|mobile|ios/.test(t)) return 'mobile';
    if (/chrome|firefox|safari|edge|browser|web/.test(t)) return 'web';
    if (/mac|windows|win|pc|linux|desktop/.test(t)) return 'desktop';
    return null;
  };

  const userMatchesDeviceCategory = (user, category) => {
    if (!category || !user.device_types) return true;
    const types = user.device_types.split(',').map((s) => s.trim());
    return types.some((t) => getDeviceCategory(t) === category);
  };

  let prefix = "";
  let select_tier = "all";
  let select_activity_condition = "all";
  let select_sort = "created_at_desc";
  let excludeDomains = true; // Exclude internal domains by default (show only external users)
  let selectedDeviceCategory = ""; // mobile | desktop | web - client-side filter
  let selectedDeviceType = "";
  let selectedTrialStatus = "";

  // Pagination
  let currentPage = 1;
  let itemsPerPage = 20;

  let user_list = {
    records: [],
  };

  let deviceTypes = [];
  let loadingDeviceTypes = false;
  let loadingUsers = false;

  let showUserDetail = false;
  let showWorkspaceDetail = false;
  let selectedUserId = null;
  let selectedWorkspaceId = null;
  let selectedUser = null; // Store the full user object for the drawer

  const loadDeviceTypes = async () => {
    if (!$token) return;
    loadingDeviceTypes = true;
    try {
      deviceTypes = await fetch_device_types($token);
    } catch (e) {
      console.error("Error fetching device types:", e);
      deviceTypes = [];
    } finally {
      loadingDeviceTypes = false;
    }
  };

  const loadUserList = async () => {
    if (!$token) return;
    loadingUsers = true;
    try {
      user_list = await fetch_user_list($token, selectedDeviceType || null, selectedTrialStatus || null);
    } catch (e) {
      console.error("Error fetching users:", e);
      user_list = { records: [] };
    } finally {
      loadingUsers = false;
    }
  };

  onMount(() => {
    loadDeviceTypes();
    loadUserList();
  });

  $: if ((selectedDeviceType !== undefined || selectedTrialStatus !== undefined) && $token) {
    loadUserList();
  }

  // Group users by user_id and aggregate workspace information
  const groupUsersByUserId = (records) => {
    const userMap = new Map();

    records.forEach((record) => {
      const userId = record.user_id;

      if (userMap.has(userId)) {
        // User already exists, add workspace info
        const existingUser = userMap.get(userId);
        existingUser.workspaces.push({
          workspace_id: record.workspace_id,
          workspace_title: record.workspace_title,
          workspace_subscription_plan_id: record.workspace_subscription_plan_id,
          workspace_subscription_plan_title: record.workspace_subscription_plan_title,
          workspace_subscription_is_trial: record.workspace_subscription_is_trial,
          workspace_subscription_id: record.workspace_subscription_id,
          workspace_plan_quantity: record.workspace_plan_quantity,
        });

        // Update latest login if this record has a more recent login
        if (
          record.last_login &&
          (!existingUser.last_login ||
            record.last_login > existingUser.last_login)
        ) {
          existingUser.last_login = record.last_login;
          existingUser.envs = record.envs;
        }
      } else {
        // New user, create entry
        const userEntry = {
          ...record,
          workspaces: [
            {
              workspace_id: record.workspace_id,
              workspace_title: record.workspace_title,
              workspace_subscription_plan_id:
                record.workspace_subscription_plan_id,
              workspace_subscription_plan_title:
                record.workspace_subscription_plan_title,
              workspace_subscription_is_trial:
                record.workspace_subscription_is_trial,
              workspace_subscription_id: record.workspace_subscription_id,
              workspace_plan_quantity: record.workspace_plan_quantity,
            },
          ],
        };

        // Remove workspace-specific fields from the main user object
        delete userEntry.workspace_id;
        delete userEntry.workspace_title;
        delete userEntry.workspace_subscription_plan_id;
        delete userEntry.workspace_subscription_plan_title;
        delete userEntry.workspace_subscription_is_trial;
        delete userEntry.workspace_subscription_id;
        delete userEntry.workspace_plan_quantity;

        userMap.set(userId, userEntry);
      }
    });

    return Array.from(userMap.values());
  };

  const determine_activity_filter = (user, condition) => {
    if (condition == "all") {
      return true;
    }
    if (condition == "more_than_one_audio") {
      return user.audio_count > 1;
    }
    return true;
  };

  const determine_domain_exclusion_filter = (user, excludeInternal) => {
    if (!excludeInternal) {
      return true; // Show all users when "Exclude internal domains" is unchecked
    }

    const email = user.email?.toLowerCase() || "";

    // Internal domains/patterns: when exclude internal is checked, exclude these
    const internalDomains = [
      "@t.com",
      "@vollume.com",
      "@tourean.com",
      "@jiricharvat.com"
    ];

    const internalPatterns = [
      "codingedgar@"
    ];

    const internalWildcardPatterns = [
      { start: "acrocz", end: "@gmail.com" },
      { start: "codingedgar", end: "@gmail.com" }
    ];

    const matchesInternalDomain = internalDomains.some(domain => email.endsWith(domain));
    const matchesInternalPattern = internalPatterns.some(pattern => email.startsWith(pattern));
    const matchesInternalWildcard = internalWildcardPatterns.some(pattern =>
      email.startsWith(pattern.start) && email.endsWith(pattern.end)
    );

    // When exclude internal is checked: include only external users
    return !(matchesInternalDomain || matchesInternalPattern || matchesInternalWildcard);
  };

  const determine_subscription_filter = (user, selected_tier) => {
    if (selected_tier == "all") {
      return true;
    }

    if (selected_tier == "basic") {
      return user.plan_id == null;
    }

    if (selected_tier == "paid") {
      return user.plan_id != null;
    }

    if (selected_tier == "pro-50") {
      return user.plan_id == "pro-50-month" || user.plan_id == "pro-50-year";
    }

    if (selected_tier == "pro-200") {
      return user.plan_id == "pro-200-month" || user.plan_id == "pro-200-year";
    }

    if (selected_tier == "pro-2000") {
      return (
        user.plan_id == "pro-2000-month" || user.plan_id == "pro-2000-year"
      );
    }

    if (selected_tier == "workspace-starter") {
      return user.workspaces.some(
        (ws) =>
          ws.workspace_subscription_plan_id == "workspace-starter-yearly",
      );
    }

    if (selected_tier == "workspace-professional-seats") {
      return user.workspaces.some(
        (ws) =>
          ws.workspace_subscription_plan_id == "workspace-professional-seats-yearly" ||
          ws.workspace_subscription_plan_id == "workspace-professional-seats-monthly",
      );
    }

    if (selected_tier == "workspace-business-seats") {
      return user.workspaces.some(
        (ws) =>
          ws.workspace_subscription_plan_id == "workspace-business-seats-yearly" ||
          ws.workspace_subscription_plan_id == "workspace-business-seats-monthly",
      );
    }

    // Legacy plan filters
    if (selected_tier == "workspace-team-seats") {
      return user.workspaces.some(
        (ws) =>
          ws.workspace_subscription_plan_id == "workspace-team-seats-yearly" ||
          ws.workspace_subscription_plan_id == "workspace-team-seats-monthly",
      );
    }

    if (selected_tier == "workspace-individual-seats") {
      return user.workspaces.some(
        (ws) =>
          ws.workspace_subscription_plan_id == "workspace-individual-seats-yearly" ||
          ws.workspace_subscription_plan_id == "workspace-individual-seats-monthly",
      );
    }

    if (selected_tier == "workspace-organisation-seats") {
      return user.workspaces.some(
        (ws) => ws.workspace_subscription_plan_id == "workspace-seats-yearly",
      );
    }

    if (selected_tier == "pro-max") {
      return user.plan_id == "pro-max-month" || user.plan_id == "pro-max-year";
    }

    if (selected_tier == "inactive-30-days") {
      return (
        new Date(user.last_login).getTime() >
        new Date().getTime() - 1000 * 60 * 60 * 24 * 30
      );
    }

    if (selected_tier == "inactive-60-days") {
      return (
        new Date(user.last_login).getTime() >
        new Date().getTime() - 1000 * 60 * 60 * 24 * 60
      );
    }

    if (selected_tier == "inactive-90-days") {
      return (
        new Date(user.last_login).getTime() >
        new Date().getTime() - 1000 * 60 * 60 * 24 * 90
      );
    }
  };

  $: grouped_users = groupUsersByUserId(user_list.records);

  // Device types shown in dropdown: filter by selected platform category
  $: filteredDeviceTypes = selectedDeviceCategory
    ? deviceTypes.filter((dt) => getDeviceCategory(dt) === selectedDeviceCategory)
    : deviceTypes;

  // When platform category changes, clear device type if it no longer matches
  $: if (selectedDeviceCategory && selectedDeviceType && getDeviceCategory(selectedDeviceType) !== selectedDeviceCategory) {
    selectedDeviceType = "";
  }

  $: filtered_users = grouped_users
    .filter((user) => {
      if (user.email && user.workspaces.length > 0) {
        return (
          (prefix
            ? user.email.toLowerCase().indexOf(prefix.toLowerCase()) >= 0 ||
              (user.name
                ? user.name.toLowerCase().indexOf(prefix.toLowerCase()) >= 0
                : false)
            : true) &&
          determine_subscription_filter(user, select_tier) &&
          determine_activity_filter(user, select_activity_condition) &&
          determine_domain_exclusion_filter(user, excludeDomains) &&
          userMatchesDeviceCategory(user, selectedDeviceCategory || null)
        );
      } else {
        return false;
      }
    })
    .sort((a, b) => {
      if (select_sort == "created_at_desc") {
        return a.created_at < b.created_at;
      }

      if (select_sort == "created_at_asc") {
        return a.created_at > b.created_at;
      }

      if (select_sort == "last_login_desc") {
        return a.last_login < b.last_login;
      }

      if (select_sort == "last_login_asc") {
        return a.last_login > b.last_login;
      }

      const email_user_a = a.email;
      const email_user_b = b.email;
      return email_user_a.localeCompare(email_user_b, "en", {
        sensitivity: "base",
      });
    });

  // Reset pagination when filters change
  $: {
    prefix;
    select_tier;
    select_activity_condition;
    select_sort;
    excludeDomains;
    selectedDeviceCategory;
    selectedDeviceType;
    selectedTrialStatus;
    currentPage = 1;
  }

  $: totalPages = Math.ceil(filtered_users.length / itemsPerPage);
  $: paginated_users = filtered_users.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Detect if any filter is active (excludeDomains true = default "exclude internal")
  $: hasActiveFilters = 
    prefix !== "" || 
    select_tier !== "all" || 
    select_activity_condition !== "all" || 
    !excludeDomains ||  // Unchecked = showing everyone (non-default)
    selectedDeviceCategory !== "" ||
    selectedDeviceType !== "" ||
    selectedTrialStatus !== "";

  const clearAllFilters = () => {
    prefix = "";
    select_tier = "all";
    select_activity_condition = "all";
    excludeDomains = true;
    selectedDeviceCategory = "";
    selectedDeviceType = "";
    selectedTrialStatus = "";
  };

  const export_user_list_as_csv = () => {
    const csv_data = filtered_users
      .map((user) => {
        return [
          user.email || "",
          user.name || "",
          user.created_at || "",
          user.workspaces.length,
          user.audio_count || 0
        ].join(",");
      })
      .join("\n");

    let csv_content =
      "data:text/csv;charset=utf-8,Email,Name,Created At,Workspace Count,Audio Count\n" +
      csv_data;

    var encoded_uri = encodeURI(csv_content);
    var link = document.createElement("a");
    link.setAttribute("href", encoded_uri);
    link.setAttribute("download", `Control User Export V2 ${new Date()}.csv`);
    document.body.appendChild(link);

    link.click();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  function handleUserClick(user) {
    selectedUser = user;
    // The open user detail is reflected in the URL (#/users/:userId) so it is
    // deep-linkable; the route sync below applies the state.
    navigate("user_list_v2", { userId: user.user_id });
  }

  function handleWorkspaceClick(workspaceId) {
    // Secondary, in-place flow: viewing a workspace from within a user's detail.
    // Kept internal (not a top-level route) to preserve prior behavior.
    selectedWorkspaceId = workspaceId;
    showWorkspaceDetail = true;
    showUserDetail = false;
  }

  function closeDrawer() {
    navigate("user_list_v2", {});
  }

  // Keep the user-detail view in sync with the URL param.
  $: syncUserRoute($route);
  function syncUserRoute(r) {
    if (r.section !== "user_list_v2") return;
    const uid = r.params.userId ?? null;
    selectedUserId = uid;
    showUserDetail = !!uid;
    showWorkspaceDetail = false;
    selectedWorkspaceId = null;
    if (!uid) selectedUser = null;
  }

  function handleOpenWorkspace(event) {
    const { workspaceId } = event.detail;
    handleWorkspaceClick(workspaceId);
  }

  function nextPage() {
    if (currentPage < totalPages) currentPage++;
  }

  function prevPage() {
    if (currentPage > 1) currentPage--;
  }
</script>

<div class="page-container">
  <div class="content-wrapper" class:hidden={showUserDetail || showWorkspaceDetail}>
    <div class="ds-page-header">
      <div>
        <h1 class="ds-page-title">User List</h1>
        <p class="ds-page-subtitle">Manage your users and their workspaces</p>
      </div>
      <div class="header-actions">
        <span class="user-count">{filtered_users.length} users</span>
        <button class="ds-btn ds-btn-primary" on:click={export_user_list_as_csv}>
          Export CSV
        </button>
      </div>
    </div>

    <div class="device-category-chips">
      {#each DEVICE_CATEGORIES as cat}
        <button
          type="button"
          class="ds-chip category-chip"
          class:active={selectedDeviceCategory === cat.value}
          on:click={() => (selectedDeviceCategory = cat.value)}
        >
          {cat.label}
        </button>
      {/each}
    </div>

    <div class="toolbar">
      <div class="search-box" class:filter-active={prefix !== ""}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="search-icon"
          ><circle cx="11" cy="11" r="8"></circle><line
            x1="21"
            y1="21"
            x2="16.65"
            y2="16.65"
          ></line></svg
        >
        <input class="ds-search" placeholder="Search by email or name..." bind:value={prefix} />
      </div>

      <div class="filters">
        <select class="ds-select" bind:value={select_tier} class:filter-active={select_tier !== "all"}>
          <option value="all">All Tiers</option>
          <option value="paid">Paid</option>
          <option value="basic">Basic</option>
          <option value="pro-50">Pro 50</option>
          <option value="pro-200">Pro 200</option>
          <option value="pro-2000">Pro 2000</option>
          <option value="pro-max">Pro Max</option>
          <option value="workspace-starter">WS Starter</option>
          <option value="workspace-professional-seats">WS Professional (New)</option>
          <option value="workspace-business-seats">WS Business (New)</option>
          <option value="workspace-team-seats">WS Professional (Legacy)</option>
          <option value="workspace-individual-seats">WS Individual (Legacy)</option>
          <option value="workspace-organisation-seats">WS Org (Legacy)</option>
          <option value="inactive-30-days">Inactive > 30d</option>
          <option value="inactive-60-days">Inactive > 60d</option>
          <option value="inactive-90-days">Inactive > 90d</option>
        </select>

        <select class="ds-select" bind:value={select_activity_condition} class:filter-active={select_activity_condition !== "all"}>
          <option value="all">All Activity</option>
          <option value="more_than_one_audio">Has Audio</option>
        </select>

        <select class="ds-select" bind:value={select_sort}>
          <option value="created_at_desc">Newest First</option>
          <option value="created_at_asc">Oldest First</option>
          <option value="last_login_desc">Recently Active</option>
          <option value="last_login_asc">Least Active</option>
        </select>

        <select class="ds-select" bind:value={selectedDeviceType} class:filter-active={selectedDeviceType !== ""}>
          <option value="">{selectedDeviceCategory ? `All ${selectedDeviceCategory} devices` : "All Device Types"}</option>
          {#each filteredDeviceTypes as deviceType}
            <option value={deviceType}>{deviceType}</option>
          {/each}
        </select>

        <select class="ds-select" bind:value={selectedTrialStatus} class:filter-active={selectedTrialStatus !== ""}>
          {#each trialStatusOptions as option}
            <option value={option.value}>{option.label}</option>
          {/each}
        </select>

        <div class="domain-filter">
          <label class="checkbox-label">
            <input 
              type="checkbox" 
              bind:checked={excludeDomains}
            />
            <span class="checkmark"></span>
            Exclude internal domains
          </label>
        </div>

        {#if hasActiveFilters}
          <button class="ds-btn ds-btn-secondary" on:click={clearAllFilters}>
            Clear All
          </button>
        {/if}
      </div>
    </div>

    {#if loadingUsers}
      <div class="ds-loading">
        <div class="ds-spinner"></div>
        <p>Loading users...</p>
      </div>
    {:else}
      <div class="table-container ds-card">
        <table class="ds-table ds-table-clickable">
          <thead>
            <tr>
              <th>User</th>
              <th>Workspaces</th>
              <th>Audio</th>
              <th>Device Types</th>
              <th>Created</th>
              <th>Last Login</th>
            </tr>
          </thead>
          <tbody>
            {#each paginated_users as user}
              <tr on:click={() => handleUserClick(user)}>
                <td>
                  <div class="user-cell">
                    <div class="avatar">
                      {#if user.avatar_url}
                        <img src={user.avatar_url} alt={user.name} />
                      {:else}
                        <span
                          >{user.name && user.name.length > 0
                            ? user.name.charAt(0).toUpperCase()
                            : "?"}</span
                        >
                      {/if}
                    </div>
                    <div class="user-info">
                      <div class="name">{user.name || "Unnamed"}</div>
                      <div class="email">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="ds-chip"
                    >{user.workspaces.length}</span
                  >
                </td>
                <td>
                  {user.audio_count}
                </td>
                <td>
                  {#if user.device_types}
                    <span class="device-types">{user.device_types.split(',').join(', ')}</span>
                  {:else}
                    <span class="text-muted">-</span>
                  {/if}
                </td>
                <td class="text-muted">
                  {formatDate(user.created_at)}
                </td>
                <td class="text-muted">
                  {user.last_login ? formatDate(user.last_login) : "Never"}
                </td>
              </tr>
            {/each}
            {#if paginated_users.length === 0}
              <tr>
                <td colspan="6" class="ds-empty">
                  No users found matching your filters.
                </td>
              </tr>
            {/if}
          </tbody>
        </table>
      </div>

      <div class="pagination">
        <div class="pagination-info">
          Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(
            currentPage * itemsPerPage,
            filtered_users.length,
          )} of {filtered_users.length} results
        </div>
        <div class="pagination-controls">
          <button class="ds-btn ds-btn-secondary ds-btn-sm" disabled={currentPage === 1} on:click={prevPage}
            >Previous</button
          >
          <span class="page-number">Page {currentPage} of {totalPages}</span>
          <button class="ds-btn ds-btn-secondary ds-btn-sm" disabled={currentPage === totalPages} on:click={nextPage}
            >Next</button
          >
        </div>
      </div>
    {/if}
  </div>

  <!-- Full-page detail view (replaces the list while open) -->
  {#if showUserDetail && selectedUserId}
    <div class="detail-page" transition:fly={{ y: 12, duration: 200 }}>
      <div class="detail-topbar">
        <button class="ds-btn ds-btn-secondary" on:click={closeDrawer}>
          ← Back to users
        </button>
      </div>
      <UserDetail
        userId={selectedUserId}
        on:openWorkspace={handleOpenWorkspace}
      />
    </div>
  {:else if showWorkspaceDetail && selectedWorkspaceId}
    <div class="detail-page" transition:fly={{ y: 12, duration: 200 }}>
      <div class="detail-topbar">
        <button
          class="ds-btn ds-btn-secondary"
          on:click={() => {
            showWorkspaceDetail = false;
            showUserDetail = true;
          }}>← Back to user</button
        >
      </div>
      <WorkspaceDetail workspaceId={selectedWorkspaceId} />
    </div>
  {/if}
</div>

<style>
  :global(body) {
    margin: 0;
  }

  .page-container {
    padding: 2rem;
    max-width: 1600px;
    margin: 0 auto;
    position: relative;
  }

  /* List is kept mounted (scroll/filters preserved) but hidden while a
     detail view is open. */
  .content-wrapper.hidden {
    display: none;
  }

  /* Full-page detail view */
  .detail-page {
    min-height: 60vh;
  }

  .detail-topbar {
    margin-bottom: 1rem;
  }

  /* Header */
  .header-actions {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .user-count {
    color: var(--ds-text-muted);
    font-weight: 500;
  }

  /* Toolbar */
  .device-category-chips {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .category-chip {
    border: 1px solid transparent;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
  }

  .category-chip:hover {
    background: var(--ds-accent-soft);
    color: var(--ds-accent);
  }

  .category-chip.active {
    background: var(--ds-accent-soft);
    color: var(--ds-accent);
    border-color: var(--ds-accent);
  }

  .toolbar {
    background: var(--ds-surface);
    padding: 1rem;
    border-radius: var(--ds-radius-lg);
    box-shadow: var(--ds-shadow-card);
    border: 1px solid var(--ds-border);
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    align-items: center;
    margin-bottom: 1.5rem;
  }

  .search-box {
    position: relative;
    flex: 1;
    min-width: 300px;
  }

  .search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--ds-text-subtle);
  }

  .search-box input {
    padding-left: 2.5rem;
  }

  /* Active filter highlight */
  select.filter-active,
  .search-box.filter-active input {
    border-color: var(--ds-accent);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
  }

  .filters {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .filters select {
    width: auto;
    cursor: pointer;
  }

  /* Domain Filter Checkbox */
  .domain-filter {
    display: flex;
    align-items: center;
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 0.875rem;
    color: var(--ds-text);
    gap: 0.5rem;
  }

  .checkbox-label input[type="checkbox"] {
    width: 1rem;
    height: 1rem;
    border: 1px solid var(--ds-border-3);
    border-radius: 0.25rem;
    cursor: pointer;
    margin: 0;
    accent-color: var(--ds-accent);
  }

  .checkbox-label input[type="checkbox"]:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
  }

  /* Table */
  .table-container {
    padding: 0;
    overflow: hidden;
  }

  .table-container .ds-empty {
    padding: 2.5rem 1rem;
    color: var(--ds-text-muted);
  }

  .text-muted {
    color: var(--ds-text-muted);
  }

  .user-cell {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .avatar {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background-color: var(--ds-border-3);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
    color: var(--ds-text-muted);
    font-weight: 600;
  }

  .avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .user-info {
    display: flex;
    flex-direction: column;
  }

  .user-info .name {
    font-weight: 500;
    color: var(--ds-text);
  }

  .user-info .email {
    font-size: 0.75rem;
    color: var(--ds-text-muted);
  }

  .device-types {
    font-size: 0.75rem;
    color: var(--ds-text-muted);
    font-family: var(--ds-mono);
  }

  /* Pagination */
  .pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 0;
    margin-top: 1rem;
  }

  .pagination-info {
    font-size: 0.875rem;
    color: var(--ds-text-muted);
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .page-number {
    font-size: 0.875rem;
    color: var(--ds-text-muted);
  }
</style>
